// utils/cacheUtils.js
import moment from 'moment'

export class RouteCache {
  constructor(ttl = 5 * 60 * 1000) { // Default 5 minutes
    this.cache = new Map();
    this.ttl = ttl;
  }
  
  /**
   * Get value from cache
   */
  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;
    
    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      return null;
    }
    
    return item.value;
  }
  
  /**
   * Set value in cache
   */
  set(key, value, customTTL) {
    const ttl = customTTL || this.ttl;
    this.cache.set(key, {
      value,
      expiry: Date.now() + ttl
    });
  }
  
  /**
   * Check if key exists and is valid
   */
  has(key) {
    return this.get(key) !== null;
  }
  
  /**
   * Delete specific key
   */
  delete(key) {
    return this.cache.delete(key);
  }
  
  /**
   * Clear entire cache
   */
  clear() {
    this.cache.clear();
  }
  
  /**
   * Get cache size
   */
  get size() {
    return this.cache.size;
  }
  
  /**
   * Clean expired entries
   */
  cleanup() {
    const now = Date.now();
    for (const [key, item] of this.cache) {
      if (now > item.expiry) {
        this.cache.delete(key);
      }
    }
  }
  
  /**
   * Get all valid entries
   */
  getAll() {
    const valid = {};
    const now = Date.now();
    
    for (const [key, item] of this.cache) {
      if (now <= item.expiry) {
        valid[key] = item.value;
      }
    }
    
    return valid;
  }
}

/**
 * Create cache key for routes
 */
export const createRouteCacheKey = (date, crewId = 'all') => {
  const dateStr = moment(date).format('YYYY-MM-DD');
  return `routes-${dateStr}-${crewId}`;
};

/**
 * Create cache key for site details
 */
export const createSiteCacheKey = (siteId) => {
  return `site-${siteId}`;
};

/**
 * Create cache key for crew hours
 */
export const createCrewHoursCacheKey = (weekStart, weekEnd) => {
  return `hours-${weekStart}-${weekEnd}`;
};

/**
 * Memoization helper for expensive calculations
 */
export const memoize = (fn, keyGenerator) => {
  const cache = new Map();
  
  return (...args) => {
    const key = keyGenerator ? keyGenerator(...args) : JSON.stringify(args);
    
    if (cache.has(key)) {
      return cache.get(key);
    }
    
    const result = fn(...args);
    cache.set(key, result);
    return result;
  };
};

/**
 * Debounce helper for search and other frequent operations
 */
export const debounce = (func, wait) => {
  let timeout;
  
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle helper for scroll and resize events
 */
export const throttle = (func, limit) => {
  let inThrottle;
  
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};